import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUIStore, useDataStore } from '../stores';
import CreateEmployeeModal from './CreateEmployeeModal';
import CreateProjectModal from './CreateProjectModal';
import EditProjectModal from './EditProjectModal';
import DeleteProjectModal from './DeleteProjectModal';
import LinkClientModal from './LinkClientModal';
import './Admin.css';

function AdminPanel() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const hasInitialized = useRef(false);
  
  // Use individual selectors to prevent unnecessary re-renders
  const stats = useDataStore(state => state.stats);
  const projects = useDataStore(state => state.projects);
  const contacts = useDataStore(state => state.contacts);
  const clients = useDataStore(state => state.clients);
  const employees = useDataStore(state => state.employees);
  const isLoading = useDataStore(state => state.isLoading);
  const isRefreshing = useDataStore(state => state.isRefreshing);
  const lastRefresh = useDataStore(state => state.lastRefresh);
  const error = useDataStore(state => state.error);
  
  // Memoized computed values to prevent infinite loops
  const ongoingProjects = useMemo(
    () => projects.filter(p => p.status === 'ongoing'),
    [projects]
  );
  
  const completedProjects = useMemo(
    () => projects.filter(p => p.status === 'completed'),
    [projects]
  );

  // Projects available for assignment (all projects except completed)
  const assignableProjects = useMemo(
    () => projects.filter(p => p.status !== 'completed'),
    [projects]
  );
  
  const pendingContacts = useMemo(
    () => contacts.filter(c => c.status === 'pending'),
    [contacts]
  );

  // Get free employees directly from the store (from API)
  const freeEmployees = useDataStore(state => state.freeEmployees);

  const assignedEmployees = useMemo(
    () => employees.filter(e => e.assigned_project_id && e.status !== 'free'),
    [employees]
  );
  
  const activeTab = useUIStore(state => state.activeTab);
  
  // Memoized tab switching function
  const setActiveTab = useCallback((tab) => {
    useUIStore.getState().setActiveTab(tab);
    // Clear error when switching tabs
    useDataStore.getState().clearError();
  }, []);
  
  // Clear error when component mounts or when tab changes
  useEffect(() => {
    // Clear error on mount and tab change
    const currentError = useDataStore.getState().error;
    if (currentError) {
      // Only clear if we have data loaded (error might be stale)
      const currentState = useDataStore.getState();
      if (currentState.dataLoaded && !currentState.isLoading) {
        useDataStore.getState().clearError();
      }
    }
  }, [activeTab]);

  // Load data from API only once on component mount
  useEffect(() => {
    if (hasInitialized.current) return;
    
    console.log('[AdminPanel] Component mounted, checking if data needs to be loaded');
    const currentState = useDataStore.getState();
    if (!currentState.dataLoaded && !currentState.isLoading) {
      console.log('[AdminPanel] Loading data...');
      hasInitialized.current = true;
      currentState.loadAllData();
    } else {
      console.log('[AdminPanel] Data already loaded or loading, skipping');
      hasInitialized.current = true;
    }
  }, []); // Empty dependency array - only run once

  // Set up automatic refresh interval
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      const currentState = useDataStore.getState();
      if (!currentState.isLoading && !currentState.isRefreshing) {
        currentState.refreshData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      clearInterval(refreshInterval);
      // Clear data when component unmounts
      useDataStore.getState().clearData();
    };
  }, []); // Empty dependency array - only run once



  // Handle contact status updates with notifications
  const handleContactStatusUpdate = useCallback(async (contactId, newStatus) => {
    try {
      await useDataStore.getState().updateContactStatus(contactId, newStatus);
      useUIStore.getState().showSuccessNotification(`Contact status updated to ${newStatus}`);
    } catch (error) {
      useUIStore.getState().showErrorNotification('Failed to update contact status');
    }
  }, []);

  // Handle logout with cleanup
  const handleLogout = useCallback(() => {
    useDataStore.getState().clearData();
    logout();
    useUIStore.getState().showSuccessNotification('Logged out successfully');
  }, [logout]);

  // Handle refresh with notifications
  const handleRefresh = useCallback(async () => {
    try {
      // Clear error before retry
      useDataStore.getState().clearError();
      await useDataStore.getState().refreshData();
      useUIStore.getState().showSuccessNotification('Data refreshed successfully');
    } catch (error) {
      useUIStore.getState().showErrorNotification('Failed to refresh data');
    }
  }, []);

  // Handle employee assignment
  const handleEmployeeAssignment = useCallback(async (employeeId, projectId) => {
    try {
      await useDataStore.getState().assignEmployeeToProject(employeeId, projectId);
      useUIStore.getState().showSuccessNotification('Employee assigned successfully');
    } catch (error) {
      useUIStore.getState().showErrorNotification('Failed to assign employee');
    }
  }, []);

  // Handle employee unassignment
  const handleEmployeeUnassignment = useCallback(async (employeeId, projectId) => {
    try {
      await useDataStore.getState().unassignEmployeeFromProject(employeeId, projectId);
      useUIStore.getState().showSuccessNotification('Employee unassigned successfully');
    } catch (error) {
      useUIStore.getState().showErrorNotification('Failed to unassign employee');
    }
  }, []);

  // Handle project deletion
  const handleDeleteProject = useCallback((projectId) => {
    useUIStore.getState().setSelectedProjectId(projectId);
    useUIStore.getState().openModal('deleteProject');
  }, []);

  // Handle project editing
  const handleEditProject = useCallback((projectId) => {
    useUIStore.getState().setSelectedProjectId(projectId);
    useUIStore.getState().openModal('editProject');
  }, []);

  const handleLinkClient = useCallback((projectId) => {
    useUIStore.getState().setSelectedProjectId(projectId);
    useUIStore.getState().openModal('linkClient');
  }, []);

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Panel</h1>
          <div className="admin-header-actions">
            <span className="admin-welcome">Welcome, {user?.username || 'Admin'}</span>
            <button onClick={handleLogout} className="admin-logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="admin-container">
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            <button
              className={activeTab === 'dashboard' ? 'active' : ''}
              onClick={() => setActiveTab('dashboard')}
            >
              📊 Dashboard
            </button>
            <button
              className={activeTab === 'ongoing' ? 'active' : ''}
              onClick={() => setActiveTab('ongoing')}
            >
              🚧 Ongoing Projects
            </button>
            <button
              className={activeTab === 'completed' ? 'active' : ''}
              onClick={() => setActiveTab('completed')}
            >
              ✅ Completed Projects
            </button>
            <button
              className={activeTab === 'contacts' ? 'active' : ''}
              onClick={() => setActiveTab('contacts')}
            >
              📧 Contact Inquiries
            </button>
            <button
              className={activeTab === 'projects' ? 'active' : ''}
              onClick={() => setActiveTab('projects')}
            >
              📁 All Projects
            </button>
            <button
              className={activeTab === 'clients' ? 'active' : ''}
              onClick={() => setActiveTab('clients')}
            >
              👤 Clients
            </button>
            <button
              className={activeTab === 'employees' ? 'active' : ''}
              onClick={() => setActiveTab('employees')}
            >
              👥 Employees
            </button>
          </nav>
        </aside>

        <main className="admin-main">
          {/* Loading State */}
          {isLoading && (
            <div className="admin-loading">
              <div className="loading-spinner"></div>
              <p>Loading dashboard data...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="admin-error">
              <div className="error-message">
                <h3>⚠️ Error Loading Data</h3>
                <p>{error}</p>
                <button onClick={handleRefresh} className="retry-btn" disabled={isRefreshing}>
                  {isRefreshing ? 'Refreshing...' : 'Retry'}
                </button>
              </div>
            </div>
          )}

          {/* Refresh Button */}
          {!isLoading && !error && (
            <div className="admin-controls">
              <div className="refresh-info">
                {lastRefresh && (
                  <span className="last-refresh">
                    Last updated: {lastRefresh.toLocaleTimeString()}
                  </span>
                )}
              </div>
              <button onClick={handleRefresh} className="refresh-btn" disabled={isRefreshing}>
                {isRefreshing ? '🔄 Refreshing...' : '🔄 Refresh Data'}
              </button>
            </div>
          )}

          {/* Main Content */}
          {!isLoading && !error && (
            <>
              {activeTab === 'dashboard' && (
            <div className="admin-dashboard">
              <div className="section-header">
                <h2>Dashboard Overview</h2>
                <button 
                  onClick={() => useUIStore.getState().openModal('createProject')}
                  className="btn-primary"
                >
                  + Create Project
                </button>
              </div>
              
              {/* Debug info - remove in production */}
              {/* {process.env.NODE_ENV === 'development' && (
                <div style={{ background: '#f0f0f0', padding: '10px', margin: '10px 0', fontSize: '12px' }}>
                  <strong>Debug - Stats Data:</strong> {JSON.stringify(stats, null, 2)}
                </div>
              )} */}
              
              <div className="stats-grid">
                <div 
                  className="stat-card clickable"
                  onClick={() => setActiveTab('projects')}
                  title="Click to view all projects"
                >
                  <div className="stat-icon">📊</div>
                  <div className="stat-info">
                    <h3>{isLoading ? '...' : (stats.totalProjects || 0)}</h3>
                    <p>Total Projects</p>
                  </div>
                </div>
                <div 
                  className="stat-card clickable"
                  onClick={() => setActiveTab('ongoing')}
                  title="Click to view ongoing projects"
                >
                  <div className="stat-icon">🚧</div>
                  <div className="stat-info">
                    <h3>{isLoading ? '...' : (stats.ongoingProjects || 0)}</h3>
                    <p>Ongoing Projects</p>
                  </div>
                </div>
                <div 
                  className="stat-card clickable"
                  onClick={() => setActiveTab('completed')}
                  title="Click to view completed projects"
                >
                  <div className="stat-icon">✅</div>
                  <div className="stat-info">
                    <h3>{isLoading ? '...' : (stats.completedProjects || 0)}</h3>
                    <p>Completed Projects</p>
                  </div>
                </div>
                <div 
                  className="stat-card clickable"
                  onClick={() => setActiveTab('contacts')}
                  title="Click to view contact inquiries"
                >
                  <div className="stat-icon">📧</div>
                  <div className="stat-info">
                    <h3>{isLoading ? '...' : (stats.pendingContacts || 0)}</h3>
                    <p>Pending Inquiries</p>
                  </div>
                </div>
                <div 
                  className="stat-card clickable"
                  onClick={() => setActiveTab('employees')}
                  title="Click to view all employees"
                >
                  <div className="stat-icon">👥</div>
                  <div className="stat-info">
                    <h3>{isLoading ? '...' : (employees.length || 0)}</h3>
                    <p>Total Employees</p>
                  </div>
                </div>
                <div 
                  className="stat-card clickable"
                  onClick={() => setActiveTab('employees')}
                  title="Click to view employees"
                >
                  <div className="stat-icon">🆓</div>
                  <div className="stat-info">
                    <h3>{isLoading ? '...' : (freeEmployees.length || 0)}</h3>
                    <p>Free Employees</p>
                  </div>
                </div>
              </div>

              <div className="dashboard-sections">
                <div className="dashboard-section">
                  <h3>Recent Ongoing Projects</h3>
                  <div className="project-list">
                    {ongoingProjects.slice(0, 3).map(project => (
                      <div 
                        key={project.id} 
                        className="project-item clickable"
                        onClick={() => navigate(`/admin/projects/${project.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="project-info">
                          <h4>{project.project_title}</h4>
                          <p>Client: {project.client_name}</p>
                          <p>Progress: {project.progress}%</p>
                        </div>
                        <div className="project-status">
                          <span className="status-badge ongoing">Ongoing</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dashboard-section">
                  <h3>Recent Contact Inquiries</h3>
                  <div className="contact-list">
                    {pendingContacts.slice(0, 3).map(contact => (
                      <div key={contact.id} className="contact-item">
                        <div className="contact-info">
                          <h4>{contact.name}</h4>
                          <p>{contact.email}</p>
                          <p>{contact.message}</p>
                        </div>
                        <span className="status-badge pending">Pending</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'ongoing' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Ongoing Projects ({ongoingProjects.length})</h2>
                <button 
                  onClick={() => useUIStore.getState().openModal('createProject')}
                  className="btn-primary"
                >
                  + Create Project
                </button>
              </div>
              <div className="projects-table">
                <table>
                  <thead>
                    <tr>
                      <th>Project Title</th>
                      <th>Client</th>
                      <th>Start Date</th>
                      <th>Expected Completion</th>
                      <th>Budget</th>
                      <th>Progress</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ongoingProjects.map(project => (
                      <tr key={project.id}>
                        <td>{project.project_title}</td>
                        <td>{project.client_name}</td>
                        <td>{new Date(project.start_date).toLocaleDateString()}</td>
                        <td>{new Date(project.expected_completion_date).toLocaleDateString()}</td>
                        <td>₹{project.budget?.toLocaleString()}</td>
                        <td>
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${project.progress}%` }}></div>
                            <span>{project.progress}%</span>
                          </div>
                        </td>
                        <td><span className="status-badge ongoing">Ongoing</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button 
                              onClick={() => handleEditProject(project.id)}
                              className="btn-edit"
                              title="Edit Project"
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              onClick={() => handleLinkClient(project.id)}
                              className="btn-link"
                              title="Link Client"
                            >
                              🔗 Link Client
                            </button>
                            <button 
                              onClick={() => handleDeleteProject(project.id)}
                              className="btn-delete"
                              title="Delete Project"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'completed' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Completed Projects ({completedProjects.length})</h2>
                <button 
                  onClick={() => useUIStore.getState().openModal('createProject')}
                  className="btn-primary"
                >
                  + Create Project
                </button>
              </div>
              <div className="projects-table">
                <table>
                  <thead>
                    <tr>
                      <th>Project Title</th>
                      <th>Client</th>
                      <th>Start Date</th>
                      <th>Completion Date</th>
                      <th>Budget</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedProjects.map(project => (
                      <tr key={project.id}>
                        <td>{project.project_title}</td>
                        <td>{project.client_name}</td>
                        <td>{new Date(project.start_date).toLocaleDateString()}</td>
                        <td>{project.completion_date ? new Date(project.completion_date).toLocaleDateString() : 'N/A'}</td>
                        <td>₹{project.budget?.toLocaleString()}</td>
                        <td><span className="status-badge completed">Completed</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button 
                              onClick={() => handleEditProject(project.id)}
                              className="btn-edit"
                              title="Edit Project"
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              onClick={() => handleLinkClient(project.id)}
                              className="btn-link"
                              title="Link Client"
                            >
                              🔗 Link Client
                            </button>
                            <button 
                              onClick={() => handleDeleteProject(project.id)}
                              className="btn-delete"
                              title="Delete Project"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className="admin-section">
              <h2>Contact Inquiries ({contacts.length})</h2>
              <div className="contacts-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Message</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map(contact => (
                      <tr key={contact.id}>
                        <td>{contact.name}</td>
                        <td>{contact.email}</td>
                        <td>{contact.phone}</td>
                        <td>{contact.message}</td>
                        <td>{contact.date}</td>
                        <td>
                          <span className={`status-badge ${contact.status}`}>
                            {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                          </span>
                        </td>
                        <td>
                          <select 
                            value={contact.status} 
                            onChange={(e) => handleContactStatusUpdate(contact.id, e.target.value)}
                            className="status-select"
                          >
                            <option value="pending">Pending</option>
                            <option value="contacted">Contacted</option>
                            <option value="resolved">Resolved</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>All Projects ({projects.length})</h2>
                <button 
                  onClick={() => useUIStore.getState().openModal('createProject')}
                  className="btn-primary"
                >
                  + Create Project
                </button>
              </div>
              <div className="projects-table">
                <table>
                  <thead>
                    <tr>
                      <th>Project Title</th>
                      <th>Client</th>
                      <th>Start Date</th>
                      <th>Completion Date</th>
                      <th>Budget</th>
                      <th>Progress</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(project => (
                      <tr key={project.id}>
                        <td>{project.project_title}</td>
                        <td>{project.client_name}</td>
                        <td>{new Date(project.start_date).toLocaleDateString()}</td>
                        <td>{project.completion_date ? new Date(project.completion_date).toLocaleDateString() : new Date(project.expected_completion_date).toLocaleDateString()}</td>
                        <td>₹{project.budget?.toLocaleString()}</td>
                        <td>
                          {project.progress < 100 ? (
                            <div className="progress-bar">
                              <div className="progress-fill" style={{ width: `${project.progress}%` }}></div>
                              <span>{project.progress}%</span>
                            </div>
                          ) : (
                            <span>100%</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${project.status}`}>
                            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button 
                              onClick={() => handleEditProject(project.id)}
                              className="btn-edit"
                              title="Edit Project"
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              onClick={() => handleLinkClient(project.id)}
                              className="btn-link"
                              title="Link Client"
                            >
                              🔗 Link Client
                            </button>
                            <button 
                              onClick={() => handleDeleteProject(project.id)}
                              className="btn-delete"
                              title="Delete Project"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'clients' && (
            <div className="admin-section">
              <h2>Clients ({clients.length})</h2>
              <div className="clients-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Address</th>
                      <th>Project Count</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map(client => (
                      <tr key={client.id}>
                        <td>{client.name || 'N/A'}</td>
                        <td>{client.phone || 'N/A'}</td>
                        <td>{client.address || 'N/A'}</td>
                        <td>{client.project_count || 0}</td>
                        <td>
                          {client.created_at 
                            ? new Date(client.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })
                            : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {clients.length === 0 && !isLoading && (
                  <div className="no-data">
                    <p>No clients found.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'employees' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Employee Management ({employees.length})</h2>
                <button 
                  onClick={() => useUIStore.getState().openModal('createEmployee')}
                  className="btn-primary"
                >
                  + Add Employee
                </button>
              </div>
              
              <div className="employee-stats">
                <div className="stat-summary">
                  <span>Total: {employees.length}</span>
                  <span>Free: {freeEmployees.length}</span>
                  <span>Assigned: {assignedEmployees.length}</span>
                </div>
              </div>

              <div className="employees-table">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Assigned Project</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(employee => (
                      <tr key={employee.id}>
                        <td>{employee.name || employee.full_name || 'N/A'}</td>
                        <td>{employee.email || 'N/A'}</td>
                        <td>{employee.phone || employee.phone_number || 'N/A'}</td>
                        <td>{employee.role || employee.position || 'N/A'}</td>
                        <td>
                          <span className={`status-badge ${employee.status || 'active'}`}>
                            {(employee.status || 'active').charAt(0).toUpperCase() + (employee.status || 'active').slice(1)}
                          </span>
                        </td>
                        <td>
                          {employee.assigned_project_id ? (
                            <span className="project-assignment">
                              Project #{employee.assigned_project_id}
                            </span>
                          ) : (
                            <span className="no-assignment">Not Assigned</span>
                          )}
                        </td>
                        <td>
                          <div className="employee-actions">
                            {employee.assigned_project_id ? (
                              <button 
                                onClick={() => handleEmployeeUnassignment(employee.id, employee.assigned_project_id)}
                                className="btn-unassign"
                                disabled={isRefreshing}
                              >
                                Unassign
                              </button>
                            ) : (
                              <select 
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleEmployeeAssignment(employee.id, parseInt(e.target.value));
                                    e.target.value = '';
                                  }
                                }}
                                className="assign-select"
                                disabled={isRefreshing}
                              >
                                <option value="">Assign to Project</option>
                                {assignableProjects.map(project => (
                                  <option key={project.id} value={project.id}>
                                    {project.project_title}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {employees.length === 0 && !isLoading && (
                  <div className="no-data">
                    <p>No employees found. Add employees to get started.</p>
                  </div>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </main>
      </div>
      
      {/* Modals */}
      <CreateEmployeeModal />
      <CreateProjectModal />
      <EditProjectModal />
      <DeleteProjectModal />
      <LinkClientModal />
    </div>
  );
}

export default AdminPanel;










